// Web Push helper riusabile da Settings page e dal banner CTA.
// Concentra qui le 4-5 chiamate fetch + il flow permission/subscribe in modo
// che le pagine non duplichino logica fragile.

const TOKEN_KEY = 'cut-catering-token'

function authHeaders() {
  const token = localStorage.getItem(TOKEN_KEY)
  return token
    ? { 'Content-Type': 'application/json', 'x-auth-token': token }
    : { 'Content-Type': 'application/json' }
}

export function isPushSupported() {
  if (typeof window === 'undefined') return false
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false
  return /iPhone|iPad|iPod/.test(navigator.userAgent)
}

export function isStandalone() {
  if (typeof window === 'undefined') return false
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    return true
  // iOS Safari pre-PWA spec
  return !!window.navigator.standalone
}

export function currentPermission() {
  if (typeof Notification === 'undefined') return 'default'
  return Notification.permission
}

export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i)
  return outputArray
}

export async function getPreferences() {
  try {
    const r = await fetch('/api/me/push-preferences', { headers: authHeaders() })
    if (!r.ok) return null
    return await r.json()
  } catch (e) {
    return null
  }
}

// Flow completo di attivazione. DEVE essere chiamato dentro un user gesture
// handler (click) per via del requirement Notification.requestPermission()
// su Safari iOS.
//
// Ritorna { ok: true } se tutto ok, altrimenti { ok: false, error, permission? }.
export async function enablePush() {
  if (!isPushSupported()) return { ok: false, error: 'unsupported' }

  let perm
  try {
    perm = await Notification.requestPermission()
  } catch (e) {
    return { ok: false, error: 'permission_failed', permission: 'default' }
  }
  if (perm !== 'granted') {
    return { ok: false, error: 'permission_denied', permission: perm }
  }

  let reg
  try {
    reg = await navigator.serviceWorker.ready
  } catch (e) {
    return { ok: false, error: 'sw_not_ready' }
  }

  let vapid
  try {
    const r = await fetch('/api/push/vapid-public-key')
    const data = await r.json()
    vapid = data && data.key
    if (!vapid) return { ok: false, error: 'vapid_not_configured' }
  } catch (e) {
    return { ok: false, error: 'vapid_fetch_failed' }
  }

  let sub
  try {
    sub = await reg.pushManager.getSubscription()
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid),
      })
    }
  } catch (e) {
    return { ok: false, error: 'subscribe_failed' }
  }

  try {
    const subJson = sub.toJSON()
    const r = await fetch('/api/me/push-subscription', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        endpoint: sub.endpoint,
        keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
        userAgent: navigator.userAgent,
      }),
    })
    if (!r.ok) return { ok: false, error: 'register_failed' }
  } catch (e) {
    return { ok: false, error: 'register_network' }
  }

  try {
    const r = await fetch('/api/me/push-preferences', {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ enabled: true }),
    })
    if (!r.ok) return { ok: false, error: 'preferences_failed' }
  } catch (e) {
    return { ok: false, error: 'preferences_network' }
  }

  return { ok: true }
}

export async function disablePush() {
  if (!isPushSupported()) return { ok: true }
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (sub) {
      try {
        await fetch('/api/me/push-subscription', {
          method: 'DELETE',
          headers: authHeaders(),
          body: JSON.stringify({ endpoint: sub.endpoint }),
        })
      } catch (e) {
        /* swallow */
      }
      try {
        await sub.unsubscribe()
      } catch (e) {
        /* swallow */
      }
    }
  } catch (e) {
    /* SW non pronto: salta */
  }
  try {
    const r = await fetch('/api/me/push-preferences', {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ enabled: false }),
    })
    if (!r.ok) return { ok: false, error: 'preferences_failed' }
  } catch (e) {
    return { ok: false, error: 'preferences_network' }
  }
  return { ok: true }
}
